import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ui';
import {
  buildReportPdf, buildReportExcel, saveBlob, printPdfBlob, safeFileName,
} from '../utils/reportExport';

// Logic behind the Share / Export dialog, shared by its desktop and mobile UI.
// A page hands over what it already shows (title, columns, rows, totals) and
// this decides the rest: PDF or Excel, portrait or landscape, preview, save,
// print, or hand the file to another app.
export function useReportShare({ title, subtitle, note, columns, rows, totalsRow, sections, summary, onClose }) {
  const { organiser, event } = useAuth();
  const toast = useToast();

  const [kind, setKind] = useState('pdf');              // pdf | excel
  const [orientation, setOrientation] = useState('landscape');
  const [density, setDensity] = useState('compact');   // compact fits far more rows on a page
  const [busy, setBusy] = useState('');                 // which action is running
  const [previewUrl, setPreviewUrl] = useState('');
  const urlRef = useRef('');

  const fileName = safeFileName([event?.EventName, title, subtitle], kind === 'pdf' ? 'pdf' : 'xlsx');

  const common = {
    title, subtitle, note, columns, rows, totalsRow, sections, summary,
    organiserName: organiser?.OrganiserName,
    eventName: event?.EventName,
  };

  const make = useCallback(async (which = kind) => (which === 'pdf'
    ? buildReportPdf({ ...common, orientation, density })
    : buildReportExcel(common)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [kind, orientation, density, columns, rows, totalsRow, sections, summary, title, subtitle, note, organiser, event]);

  // Preview is PDF-only: a spreadsheet has nothing to show until it is opened.
  useEffect(() => {
    let dead = false;
    if (kind !== 'pdf') { setPreviewUrl(''); return undefined; }
    setBusy('preview');
    make('pdf')
      .then(blob => {
        if (dead) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = URL.createObjectURL(blob);
        setPreviewUrl(urlRef.current);
      })
      .catch(() => { if (!dead) toast('Could not build the preview'); })
      .finally(() => { if (!dead) setBusy(''); });
    return () => { dead = true; };
  }, [kind, orientation, density, make, toast]);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const run = async (action, fn) => {
    setBusy(action);
    try { await fn(); } catch { toast('Could not create the file'); } finally { setBusy(''); }
  };

  const save = (which = kind) => run('save', async () => {
    const blob = await make(which);
    saveBlob(blob, safeFileName([event?.EventName, title, subtitle], which === 'pdf' ? 'pdf' : 'xlsx'));
    toast(which === 'pdf' ? 'PDF saved' : 'Excel saved');
  });

  const print = () => run('print', async () => {
    printPdfBlob(await buildReportPdf({ ...common, orientation, density }));
  });

  // Phones can hand the file to WhatsApp / Drive / anything through the OS
  // sheet; a desktop browser only offers it where the browser supports it.
  const canShareFile = typeof navigator !== 'undefined' && !!navigator.canShare;
  const shareFile = () => run('share', async () => {
    const blob = await make();
    const file = new File([blob], fileName, { type: blob.type });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title, text: `${title}${subtitle ? ' — ' + subtitle : ''}` });
    } else {
      saveBlob(blob, fileName);
      toast('Saved — attach it from your downloads');
    }
  });

  return {
    kind, setKind, orientation, setOrientation, density, setDensity, busy, previewUrl, fileName,
    // A multi-block report (the Brief) counts every block's rows.
    rowCount: sections ? sections.reduce((n, s) => n + (s.rows ? s.rows.length : 0), 0) : (rows || []).length,
    columnCount: sections ? sections.length : (columns || []).length,
    sections,
    title, subtitle, event, organiser,
    save, print, shareFile, canShareFile, close: onClose,
  };
}
